import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Chip,
  Card,
  CardContent,
  Button,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  CircularProgress,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,

  Save as SaveIcon,
  Class as ClassIcon,
  LibraryBooks as LibraryBooksIcon,

  People as PeopleIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import { toast } from 'react-toastify';
import { adminHasPermission } from '../../utils/adminPermissions';
import TutorGroupAssignment from './TutorGroupAssignment';

const ROLE_TUTOR = 'Tutor';
const ROLE_MOBILE_TUTOR = 'Mobile Tutor';
const ROLE_TUTOR_ASSISTANT = 'Tutor Assistant';

function sameIdList(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((id, index) => id === b[index]);
}

function sameSubjectMap(a, b) {
  const aKeys = Object.keys(a || {});
  const bKeys = Object.keys(b || {});
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => sameIdList(a[key], b[key]));
}

const TutorForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const canUpdateTutors = adminHasPermission('update-tutors');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [role, setRole] = useState(ROLE_TUTOR);

  const [courses, setCourses] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableSchoolYears, setAvailableSchoolYears] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentsLoading, setStudentsLoading] = useState(false);

  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [selectedSubjectIdsByCourseId, setSelectedSubjectIdsByCourseId] = useState({});
  const [preferredSchoolYearIds, setPreferredSchoolYearIds] = useState([]);
  const [preferredSubjectIds, setPreferredSubjectIds] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [selectedStudentsMeta, setSelectedStudentsMeta] = useState({});
  // Groups chosen while creating a tutor. There is no user id to attach them to yet, so they are
  // held here and POSTed once the tutor exists.
  const [stagedGroups, setStagedGroups] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const studentFetchSeqRef = useRef(0);

  const isMobileTutor = role === ROLE_MOBILE_TUTOR;
  const isTutorAssistant = role === ROLE_TUTOR_ASSISTANT;
  const preferredSchoolYearKey = useMemo(
    () => preferredSchoolYearIds.join(','),
    [preferredSchoolYearIds]
  );
  const preferredSubjectKey = useMemo(() => preferredSubjectIds.join(','), [preferredSubjectIds]);

  useEffect(() => {
    if (!canUpdateTutors) {
      toast.error('You do not have permission to manage tutors.');
      navigate('/admin/tutors');
      return;
    }
    fetchData();
  }, [canUpdateTutors, navigate, id]);

  const fetchStudents = useCallback(
    async (search = '') => {
      if (role !== ROLE_MOBILE_TUTOR) return;
      const seq = ++studentFetchSeqRef.current;
      setStudentsLoading(true);
      try {
        const params = { search, limit: 50 };
        if (preferredSchoolYearIds.length > 0) {
          params.school_year_ids = preferredSchoolYearIds;
        }
        if (preferredSubjectIds.length > 0) {
          params.subject_ids = preferredSubjectIds;
        }
        const res = await api.get('admin/tutors/students-for-assignment', { params });
        if (seq !== studentFetchSeqRef.current) return;
        if (res.data?.success) {
          setStudentOptions(res.data.data || []);
        } else {
          toast.error(res.data?.message || 'Could not load students for assignment.');
        }
      } catch (e) {
        if (seq !== studentFetchSeqRef.current) return;
        const status = e.response?.status;
        const msg =
          status === 502
            ? 'Server error loading students (502). The API may be down — check api.11plusmagic.co.uk.'
            : e.response?.data?.message ||
              e.response?.data?.error ||
              'Could not load students for assignment.';
        toast.error(msg);
      } finally {
        if (seq === studentFetchSeqRef.current) {
          setStudentsLoading(false);
        }
      }
    },
    [preferredSchoolYearIds, preferredSubjectIds, role]
  );

  useEffect(() => {
    if (!isMobileTutor) {
      studentFetchSeqRef.current += 1;
      setStudentsLoading(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      void fetchStudents(studentSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [studentSearch, isMobileTutor, preferredSchoolYearKey, preferredSubjectKey, fetchStudents]);

  const fetchCoursesLookup = useCallback(async () => {
    try {
      const params = {};
      if (preferredSchoolYearIds.length > 0) {
        params.school_year_ids = preferredSchoolYearIds;
      }
      if (preferredSubjectIds.length > 0) {
        params.subject_ids = preferredSubjectIds;
      }
      const res = await api.get('admin/tutors/courses-for-assignment', { params });
      if (res.data?.success) {
        const nextCourses = res.data.data.courses || [];
        const nextSubjects = res.data.data.subjects || [];
        const nextSchoolYears = res.data.data.school_years || [];
        setCourses(nextCourses);
        setAvailableSubjects(nextSubjects);
        if (nextSchoolYears.length > 0) {
          setAvailableSchoolYears(nextSchoolYears);
        }

        const validSubjectIds = new Set(nextSubjects.map((s) => s.id));
        setPreferredSubjectIds((prev) => {
          const filtered = prev.filter((id) => validSubjectIds.has(id));
          return sameIdList(prev, filtered) ? prev : filtered;
        });

        const validCourseIds = new Set(nextCourses.map((c) => c.id));
        setSelectedCourseIds((prev) => {
          const filtered = prev.filter((id) => validCourseIds.has(id));
          return sameIdList(prev, filtered) ? prev : filtered;
        });
        setSelectedSubjectIdsByCourseId((prev) => {
          const next = {};
          Object.entries(prev).forEach(([courseId, subjectIds]) => {
            if (!validCourseIds.has(Number(courseId))) return;
            const filtered = subjectIds.filter((sid) => validSubjectIds.has(sid));
            if (filtered.length > 0) {
              next[courseId] = filtered;
            }
          });
          return sameSubjectMap(prev, next) ? prev : next;
        });
      } else {
        toast.error(res.data?.message || 'Could not load courses for assignment.');
      }
    } catch (e) {
      const status = e.response?.status;
      const msg =
        status === 502
          ? 'Server error loading courses (502). The API may be down — check api.11plusmagic.co.uk.'
          : e.response?.data?.message ||
            e.response?.data?.error ||
            'Could not load courses for assignment.';
      toast.error(msg);
    }
  }, [preferredSchoolYearIds, preferredSubjectIds]);

  useEffect(() => {
    void fetchCoursesLookup();
  }, [fetchCoursesLookup]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await fetchCoursesLookup();

      if (isEdit) {
        const resTutor = await api.get(`admin/tutors/${id}`);
        if (resTutor.data?.success) {
          const tutor = resTutor.data.data;
          setFormData({
            firstName: tutor.first_name,
            lastName: tutor.last_name,
            email: tutor.email,
          });
          setRole(
            [ROLE_MOBILE_TUTOR, ROLE_TUTOR_ASSISTANT].includes(tutor.role_name)
              ? tutor.role_name
              : ROLE_TUTOR
          );
          setSelectedCourseIds(tutor.course_ids || []);
          setSelectedSubjectIdsByCourseId(tutor.course_subject_ids_by_course || {});
          setPreferredSchoolYearIds(tutor.preferred_school_year_ids || []);
          setPreferredSubjectIds(tutor.preferred_subject_ids || []);
          const studentIds = (tutor.student_ids || []).map((n) => Number(n));
          setSelectedStudentIds(studentIds);
          if (studentIds.length) {
            const resStudents = await api.get('admin/tutors/students-for-assignment', {
              params: {
                search: '',
                limit: 100,
                ...(tutor.preferred_school_year_ids?.length
                  ? { school_year_ids: tutor.preferred_school_year_ids }
                  : {}),
                ...(tutor.preferred_subject_ids?.length
                  ? { subject_ids: tutor.preferred_subject_ids }
                  : {}),
              },
            });
            const map = {};
            (resStudents.data?.data || []).forEach((s) => {
              map[s.id] = s;
            });
            setSelectedStudentsMeta(map);
          }
        } else {
          toast.error('Failed to load tutor details');
          navigate('/admin/tutors');
        }
      }
    } catch (e) {
      toast.error('Failed to load form data');
      if (isEdit) navigate('/admin/tutors');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCourse = (courseId) => {
    setSelectedCourseIds((prev) => {
      const isSelected = prev.includes(courseId);
      const next = isSelected ? prev.filter((cid) => cid !== courseId) : [...prev, courseId];

      const courseKey = String(courseId);
      setSelectedSubjectIdsByCourseId((prevMap) => {
        const copy = { ...prevMap };
        if (isSelected) {
          delete copy[courseKey];
        } else {
          copy[courseKey] = copy[courseKey] ?? [];
        }
        return copy;
      });

      return next;
    });
  };

  const handleSelectAllCourses = (checked) => {
    if (checked) {
      setSelectedCourseIds(courses.map((c) => c.id));
    } else {
      setSelectedCourseIds([]);
      setSelectedSubjectIdsByCourseId({});
    }
  };

  const handleToggleSubjectInCourse = (courseId, subjectId) => {
    const courseKey = String(courseId);
    setSelectedSubjectIdsByCourseId((prev) => {
      const current = prev[courseKey] ?? [];
      const next = current.includes(subjectId)
        ? current.filter((sid) => sid !== subjectId)
        : [...current, subjectId];
      return { ...prev, [courseKey]: next };
    });
  };

  const handleTogglePreference = (prefId, list, setList) => {
    setList((prev) =>
      prev.includes(prefId) ? prev.filter((item) => item !== prefId) : [...prev, prefId]
    );
  };

  const handleToggleStudent = (student) => {
    const sid = Number(student.id);
    setSelectedStudentIds((prev) => {
      const exists = prev.includes(sid);
      return exists ? prev.filter((x) => x !== sid) : [...prev, sid];
    });
    setSelectedStudentsMeta((prev) => ({ ...prev, [sid]: student }));
  };

  const visibleStudentIds = studentOptions.map((student) => Number(student.id));
  const allVisibleStudentsSelected =
    visibleStudentIds.length > 0 &&
    visibleStudentIds.every((sid) => selectedStudentIds.includes(sid));
  const someVisibleStudentsSelected =
    visibleStudentIds.some((sid) => selectedStudentIds.includes(sid)) &&
    !allVisibleStudentsSelected;

  const handleToggleAllVisibleStudents = () => {
    if (allVisibleStudentsSelected) {
      setSelectedStudentIds((prev) => prev.filter((sid) => !visibleStudentIds.includes(sid)));
      return;
    }

    setSelectedStudentIds((prev) => [...new Set([...prev, ...visibleStudentIds])]);
    setSelectedStudentsMeta((prev) => {
      const next = { ...prev };
      studentOptions.forEach((student) => {
        next[Number(student.id)] = student;
      });
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const { firstName, lastName, email } = formData;
    if (!firstName || !lastName || !email) {
      toast.error('Please fill in all required fields.');
      return;
    }

    const courseSubjects = selectedCourseIds.map((course_id) => ({
      course_id,
      subject_ids: selectedSubjectIdsByCourseId[String(course_id)] ?? [],
    }));

    const payload = {
      first_name: firstName,
      last_name: lastName,
      email,
      role,
      preferred_school_year_ids: preferredSchoolYearIds,
      preferred_subject_ids: preferredSubjectIds,
      course_subjects: courseSubjects,
      student_ids: isMobileTutor ? selectedStudentIds : [],
    };

    setSaving(true);
    try {
      const res = isEdit
        ? await api.put(`admin/tutors/${id}`, payload)
        : await api.post('admin/tutors', payload);

      if (res.data?.success) {
        // Apply groups staged during creation. Sequential, because assigning a lead can replace an
        // incumbent and the server enforces "helpers require a lead" — order matters.
        const newTutorId = isEdit ? id : res.data?.data?.id;
        if (!isEdit && newTutorId && stagedGroups.length > 0) {
          const leadsFirst = [...stagedGroups].sort((a, b) =>
            a.role === b.role ? 0 : a.role === 'tutor' ? -1 : 1
          );
          let failed = 0;
          for (const g of leadsFirst) {
            try {
              await api.post(`admin/tutors/${newTutorId}/groups`, {
                course_plan_id: g.course_plan_id,
                role: g.role,
                replace_existing: true,
              });
            } catch {
              failed += 1;
            }
          }
          if (failed > 0) {
            toast.warn(
              `Tutor created, but ${failed} group assignment${failed > 1 ? 's' : ''} failed. Add them from the edit screen.`
            );
          }
        }

        toast.success(
          isEdit
            ? 'Tutor updated successfully'
            : 'Tutor created successfully. Credentials sent via email.'
        );
        navigate('/admin/tutors');
      } else {
        toast.error(res.data?.message || 'Failed to save tutor');
      }
    } catch (error) {
      const apiErr =
        error.response?.data?.data?.error ||
        error.response?.data?.message ||
        error.response?.data?.error;
      toast.error(typeof apiErr === 'string' ? apiErr : 'Failed to save tutor');
    } finally {
      setSaving(false);
    }
  };

  const allCoursesSelected = courses.length > 0 && selectedCourseIds.length === courses.length;

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}
    >
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/admin/tutors')}
        sx={{
          mb: 3,
          color: '#5a6c7d',
          fontWeight: 600,
          '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
        }}
      >
        Back to Tutor List
      </Button>

      <Box sx={{ mb: 4 }}>
        <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#111827', lineHeight: 1.3, mb: 2 }}>
          {isEdit ? 'Edit Tutor Profile' : 'Add New Tutor'}
        </Typography>
        <Typography variant="body1" sx={{ color: '#5a6c7d', ml: 1 }}>
          {isEdit
            ? `Update profile details and assignments for ${formData.firstName} ${formData.lastName}.`
            : 'Create a new tutor account. Login credentials and portal URL will be sent via email.'}
        </Typography>
      </Box>

      <Card
        sx={{
          borderRadius: 4,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)',
          mb: 4,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSave}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2d3748' }}>
                  1. Profile Details
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="First Name"
                  required
                  fullWidth
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Last Name"
                  required
                  fullWidth
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  required
                  fullWidth
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  helperText={!isEdit && 'Password will be automatically generated and emailed.'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="tutor-role-label">Role</InputLabel>
                  <Select
                    labelId="tutor-role-label"
                    label="Role"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  >
                    <MenuItem value={ROLE_TUTOR}>Tutor</MenuItem>
                    <MenuItem value={ROLE_TUTOR_ASSISTANT}>Tutor Assistant</MenuItem>
                    <MenuItem value={ROLE_MOBILE_TUTOR}>Mobile Tutor</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {isMobileTutor
                    ? 'Mobile Tutors use the same tutor panel and see mobile worksheet / paper / mock progress for assigned students.'
                    : isTutorAssistant
                      ? 'Tutor Assistants host live classes for the subjects assigned to them on a group, and publish that subject\u2019s content themselves. Anything outside their subjects is uploaded for the lead tutor to approve.'
                      : 'Tutors lead groups: they approve their assistants’ content before an admin reviews it, and host live classes for the subjects they hold on each group.'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 600, color: '#2d3748' }}>
                  2. Preferred Levels & Subjects
                  {isMobileTutor && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                      (optional for Mobile Tutor)
                    </Typography>
                  )}
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ClassIcon color="action" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Preferred School Years
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {isMobileTutor
                    ? 'Optional. When selected, subject, student, and course lists below are filtered.'
                    : 'Select school years this tutor teaches.'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {availableSchoolYears.map((year) => (
                    <Chip
                      key={year.id}
                      label={year.name}
                      onClick={() =>
                        handleTogglePreference(year.id, preferredSchoolYearIds, setPreferredSchoolYearIds)
                      }
                      color={preferredSchoolYearIds.includes(year.id) ? 'primary' : 'default'}
                      variant={preferredSchoolYearIds.includes(year.id) ? 'filled' : 'outlined'}
                    />
                  ))}
                  {availableSchoolYears.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Loading school years...
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LibraryBooksIcon color="action" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Preferred Subjects
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {availableSubjects.map((subject) => (
                    <Chip
                      key={subject.id}
                      label={subject.name}
                      onClick={() =>
                        handleTogglePreference(subject.id, preferredSubjectIds, setPreferredSubjectIds)
                      }
                      color={preferredSubjectIds.includes(subject.id) ? 'primary' : 'default'}
                      variant={preferredSubjectIds.includes(subject.id) ? 'filled' : 'outlined'}
                    />
                  ))}
                  {availableSubjects.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Loading subjects...
                    </Typography>
                  )}
                </Box>
              </Grid>

              {isMobileTutor && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 600, color: '#2d3748' }}>
                    3. Assign Students (mobile progress)
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon color="action" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Students this Mobile Tutor can monitor
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search students by name or email"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  {selectedStudentIds.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {selectedStudentIds.map((sid) => {
                        const meta =
                          selectedStudentsMeta[sid] || studentOptions.find((s) => s.id === sid);
                        return (
                          <Chip
                            key={sid}
                            label={meta?.name || meta?.email || `Student #${sid}`}
                            onDelete={() =>
                              setSelectedStudentIds((prev) => prev.filter((x) => x !== sid))
                            }
                            color="primary"
                          />
                        );
                      })}
                    </Box>
                  )}
                  <Paper
                    variant="outlined"
                    sx={{ maxHeight: 280, overflow: 'auto', p: 1, borderRadius: 2 }}
                  >
                    {studentsLoading ? (
                      <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : (
                      <List dense>
                        {studentOptions.length > 0 && (
                          <ListItem
                            disablePadding
                            sx={{
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              mb: 0.5,
                              pb: 0.5,
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={allVisibleStudentsSelected}
                                  indeterminate={someVisibleStudentsSelected}
                                  onChange={handleToggleAllVisibleStudents}
                                />
                              }
                              label={`Select all (${studentOptions.length})`}
                              sx={{ fontWeight: 600 }}
                            />
                          </ListItem>
                        )}
                        {studentOptions.map((student) => (
                          <ListItem key={student.id} disablePadding>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedStudentIds.includes(Number(student.id))}
                                  onChange={() => handleToggleStudent(student)}
                                />
                              }
                              label={`${student.name}${student.email ? ` (${student.email})` : ''}${
                                student.school_year ? ` · ${student.school_year}` : ''
                              }`}
                            />
                          </ListItem>
                        ))}
                        {studentOptions.length === 0 && (
                          <Typography color="text.secondary" sx={{ p: 2 }}>
                            No students found.
                          </Typography>
                        )}
                      </List>
                    )}
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 600, color: '#2d3748' }}>
                  {isMobileTutor ? '4. Optional: Assign Specific Courses' : '3. Optional: Assign Specific Courses'}
                </Typography>
                {/* These are the outer limit on group staffing: a tutor can only be given a group
                    subject they are ticked for on that course here, and a class is refused if they
                    are not. Nothing here staffs a group on its own — that is the Staff tab. */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  The subjects ticked under each course are the ones this tutor may teach on it. A
                  group can only give them a subject from this list, on Resource Allocation → Staff.
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>

              <Grid item xs={12}>
                {loading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allCoursesSelected}
                          indeterminate={
                            selectedCourseIds.length > 0 && selectedCourseIds.length < courses.length
                          }
                          onChange={(e) => handleSelectAllCourses(e.target.checked)}
                        />
                      }
                      label="Select all courses"
                      sx={{ mb: 1 }}
                    />
                    <Paper
                      variant="outlined"
                      sx={{ maxHeight: 300, overflow: 'auto', p: 2, borderRadius: 2 }}
                    >
                      <List dense>
                        {courses.map((course) => {
                          const isSelected = selectedCourseIds.includes(course.id);
                          const selectedSubjectIds =
                            selectedSubjectIdsByCourseId[String(course.id)] ?? [];

                          return (
                            <Box key={course.id}>
                              <ListItem disablePadding>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() => handleToggleCourse(course.id)}
                                    />
                                  }
                                  label={course.name}
                                />
                              </ListItem>

                              {isSelected &&
                                Array.isArray(course.subjects) &&
                                course.subjects.length > 0 && (
                                  <Box sx={{ pl: 4, pr: 1, pb: 1 }}>
                                    <Grid container spacing={1}>
                                      {/* course_subject holds one row per mode x location, so the
                                          same subject can arrive several times. Deduping here as
                                          well as server-side keeps React keys unique even against
                                          an older API that has not been deployed yet. */}
                                      {Array.from(
                                        new Map(course.subjects.map((sub) => [sub.id, sub])).values()
                                      ).map((subject) => (
                                        <Grid item key={subject.id}>
                                          <Chip
                                            label={subject.name}
                                            onClick={() =>
                                              handleToggleSubjectInCourse(course.id, subject.id)
                                            }
                                            color={
                                              selectedSubjectIds.includes(subject.id)
                                                ? 'primary'
                                                : 'default'
                                            }
                                            variant={
                                              selectedSubjectIds.includes(subject.id)
                                                ? 'filled'
                                                : 'outlined'
                                            }
                                            size="small"
                                            sx={{ borderRadius: '6px' }}
                                          />
                                        </Grid>
                                      ))}
                                    </Grid>
                                  </Box>
                                )}
                            </Box>
                          );
                        })}
                      </List>
                      {courses.length === 0 && (
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          No courses available.
                        </Typography>
                      )}
                    </Paper>
                  </>
                )}
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/admin/tutors')}
                    disabled={saving}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={saving || !canUpdateTutors}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      px: 4,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                      },
                    }}
                  >
                    {saving ? 'Saving...' : isEdit ? 'Update Tutor' : 'Create Tutor'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>

          {/* Groups are for classroom tutors. Mobile Tutors work student-by-student through the
              individual picker above, so the two assignment models stay separate.

              Rendered outside the <form> on purpose: a <button> without an explicit type submits
              its parent form, and this panel's buttons must never save the tutor. */}
          {!isMobileTutor && (
            <>
              <Divider sx={{ my: 4 }} />
              <TutorGroupAssignment
                tutorId={isEdit ? Number(id) : null}
                staged={stagedGroups}
                onStagedChange={setStagedGroups}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TutorForm;
